/**
 * Copyright IBM Corp. 2022, 2026
 */

'use client'

import { Card } from '../../components/card'
import classNames from 'classnames'
import type { RelatedContentProps } from './types'
import { useMDSLink } from '../../utils/mds-context'
import s from './style.module.css'
import { Text } from '../../components'

const RelatedContent = ({
	appearance = 'transparent',
	headline,
	description,
	cards,
	cta,
}: RelatedContentProps) => {
	const Link = useMDSLink()

	return (
		<div
			className={classNames(s.root, s[appearance], {
				['mds-dark-mode']: appearance === 'dark',
			})}
		>
			<div className={s.wrapper}>
				<div className={s.textStack}>
					<Text.DisplayExpressive
						tag="h2"
						color="primary"
						className={s.headline}
						size="400"
					>
						{headline}
					</Text.DisplayExpressive>
					{description ? (
						<Text.Body size="300" color="faint" tag="p">
							{description}
						</Text.Body>
					) : null}
				</div>
				<div className={s.cards} data-testid="wpl-cards-container">
					{cards.map((card, i) => (
						<Card
							key={i}
							href={card.link}
							content={{
								...(card.thumbnail && {
									thumbnail: {
										alt: card.thumbnail.alt,
										src: card.thumbnail.src,
										aspectRatio: '16/9',
									},
								}),
								eyebrow: card.meta,
								heading: card.heading,
								description: card.description,
								showArrow: true,
							}}
						/>
					))}
				</div>

				{cta ? (
					<div className={s.cta} data-testid="wpl-cta-container">
						<Link href={cta.href}>{cta.text}</Link>
					</div>
				) : null}
			</div>
		</div>
	)
}

RelatedContent.displayName = 'RelatedContent'

export { RelatedContent }
